import CommunicationHandler, { IEvent, SubscriptionHandler } from '@mirage-projects/communication-library';
import {
    StepData,
    STEP_ACTION_TYPE,
    EventType,
    StepEvent,
    ResolveConditionFunction,
    ParamObj,
    GlobalData,
    StepEventData,
    isStepEventFactory,
    isStepEventData,
} from '@mirage-projects/cw-models';

import { logService } from './Log';
import { DebugInfo } from './MocksTypes';

/** Partial StepData with required action */
type StepParamData = Partial<StepData> & Pick<StepData, 'action'>;

/**
 * Implements each process step
 */
export class Step implements StepData {
    action: STEP_ACTION_TYPE;
    type?: EventType;
    runDelay?: number;
    eventFilter?: EventType;
    event?: StepEvent;
    resolveCondition?: ResolveConditionFunction;

    private subsHandler?: SubscriptionHandler;

    constructor(data: StepParamData) {
        this.action = data.action;
        this.event = data.event;
        this.eventFilter = data.eventFilter;
        this.runDelay = data.runDelay ?? 0;
        this.resolveCondition = data.resolveCondition;
        this.type = data.type;
    }

    /**
     * Runs the step
     * @param communicationHandler Handles the communication events
     * @param params params to override default values
     * @param globalData global data to be accessed by the step
     * @param prevData previous step data result
     * @param debugInfo  info to debugging this function
     */
    async run(
        communicationHandler: CommunicationHandler,
        params: ParamObj,
        globalData?: GlobalData,
        prevData?: GlobalData,
        debugInfo?: DebugInfo
    ): Promise<IEvent | StepEventData | undefined> {
        if (this.runDelay) {
            await this.timeoutToPromise(this.runDelay);
        }

        if (!this.event) {
            return;
        }

        if (this.action === STEP_ACTION_TYPE.SEND) {
            let resultEvent = this.event;

            if (isStepEventFactory(this.event)) {
                resultEvent = this.event(params, globalData, prevData);
            } else {
                if (params) {
                    resultEvent = this.applyParams(params);
                }
            }

            if (!isStepEventData(resultEvent)) {
                return;
            }

            logService.debug(
                `${this.loggerMessage(resultEvent, debugInfo?.process.name, 'run')},globalData: ${JSON.stringify(globalData)}`
            );

            if (resultEvent) {
                await communicationHandler.sendEvent(resultEvent);
            }
            logService.debug(this.loggerMessage(resultEvent, debugInfo?.process.name, 'finish')); // logger

            return resultEvent;
        }

        logService.debug(this.loggerMessage(this.eventFilter, debugInfo?.process.name, 'run')); // logger
        const result = await this.onEventsToPromise(communicationHandler);
        logService.debug(this.loggerMessage(this.eventFilter, debugInfo?.process.name, 'finish')); // logger

        if (this.resolveCondition && !this.resolveCondition(result, prevData)) {
            throw new Error(`step: resolve condition not passed (${result})`);
        }

        return result;
    }

    /**
     * Creates a copy of this step
     */
    clone(): Step {
        const clonedStep = new Step(this);

        if (clonedStep.event && isStepEventData(clonedStep.event) && clonedStep?.event?.message) {
            clonedStep.event.message = { ...clonedStep.event.message };
        }
        return clonedStep;
    }

    private onEventsToPromise(communicationHandler: CommunicationHandler): Promise<IEvent> {
        return new Promise(
            (resolve) =>
                (this.subsHandler = communicationHandler.onEvents(this.eventFilter ?? '').subscribe((event) => {
                    this.subsHandler?.cancel(); // cancel to avoid errors with step rerun
                    resolve(event);
                }))
        );
    }

    private timeoutToPromise(delay: number): Promise<void> {
        return new Promise((resolve) => setTimeout(resolve, delay));
    }

    private applyParams(paramObj: ParamObj): StepEventData {
        const resultEvent = structuredClone(this.event) as StepEventData;

        if (!resultEvent || !paramObj) {
            return resultEvent;
        }

        if (!resultEvent?.message || !paramObj?.message) {
            return resultEvent;
        }
        Object.assign(resultEvent.message, paramObj.message);

        return resultEvent;
    }

    private loggerMessage(data: any, name: string | undefined, type: 'run' | 'finish'): string {
        const message = JSON.stringify(data);

        return `process: ${name ?? ''} > ${type} step ${this.action} ${message}`;
    }
}
